export class ApiError extends Error {
  status: number;
  code: string;
  details?: any;
  constructor(status: number, code: string, message: string, details?: any) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const lostListeners = new Set<() => void>();

export const onAuthLost = (fn: () => void): (() => void) => { lostListeners.add(fn); return () => { lostListeners.delete(fn); }; };
export const setAccessToken = (token: string | null): void => { accessToken = token; };
export const hasSession = (): boolean => accessToken !== null;

export function visitorId(): string {
  try {
    let id = localStorage.getItem('gs_visitor');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('gs_visitor', id); }
    return id;
  } catch {
    return 'anonymous';
  }
}

/** Exchanges the http-only refresh cookie for a new access token. */
export function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch('/v1/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok || !body?.accessToken) { accessToken = null; return false; }
        accessToken = body.accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
}

export type Query = Record<string, string | number | boolean | null | undefined>;
export interface RequestOptions { method?: string; body?: unknown; query?: Query; signal?: AbortSignal }

export function buildUrl(path: string, query?: Query): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { 'X-Visitor-Id': visitorId() };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    credentials: 'include', headers, signal: opts.signal,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

export async function api<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts);
  if (res.status === 401 && accessToken && !path.startsWith('/v1/auth/')) {
    if (await refreshSession()) res = await send(path, opts);
    else lostListeners.forEach((fn) => fn());
  }
  const text = await res.text();
  let data: any = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'http_error', e.message ?? res.statusText, e.details);
  }
  return data as T;
}

/** Downloads a protected file (CSV, PDF) with the current session and opens or saves it. */
export async function download(path: string, filename: string, query?: Query): Promise<void> {
  const res = await send(path, { query });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', res.statusText);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function openDocument(path: string): Promise<void> {
  const win = window.open('', '_blank');
  const res = await send(path, {});
  const html = await res.text();
  if (win) { win.document.open(); win.document.write(html); win.document.close(); }
}

export const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]+,/, ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});
